import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, Branding, ModalContainer, Page, useGlobalController, useModel, useRoute } from "@bcm-dev/bcms-vite";
import FooterView from "compid/shared/views/FooterView";
import HeaderView from "compid/shared/views/HeaderView";
import React, { useEffect } from 'react';

import { AddBiller, Autopay, Bill, Biller, BillerGroup, Payment, PaymentInquiry, Profile, Reminder } from 'model';

import modals from "compid/custom-ui/CustomModal/components";
import "./styles.css";

/**
 * @function PageView 
 * @param {React.Component} children - React components that's being wrapped by the page view.
 * @returns {React.Component}
 * @example
 * <PageView>
 *  <PageLayoutContainer>
 *    <CenterColumnContainer>
 *      <div className="form-group flex-container flex-vertical">
 *        <UISearch placeHolder="Search for a biller" />
 *        <ButtonContainer>
 *          <UIButton className="secondary-button">Cancel</UIButton>
 *          <UIButton className="primary-button">Next</UIButton>
 *        </ButtonContainer>
 *      </div>
 *    </CenterColumnContainer>
 *  </PageLayoutContainer>
 * </PageView>
 */
export default function PageView(props) {
  const model = useModel();
  const controller = useGlobalController();
  const navigate = useRoute();

  BCMSRecord.globalControllerFor([Payment, Bill, Biller,AddBiller, BillerGroup, Autopay, Profile ,Reminder, PaymentInquiry, Reminder], controller);

  //var isIntegrated = model.branding.options.demo_integrated_rxp22 === "true" ?  true : false;

  // The ".page-sub-header" is set to a css "sticky" top position of 75px by default.
  // Adjust the position here depending on branding for integration.
  useEffect(() => {
    /*if(isIntegrated) {
      const pageSubHeader = document.querySelector('.page-sub-header');
      if(pageSubHeader) { pageSubHeader.style.top = 0 + "px"; }
    }*/
    try{
      if(window.parent){
        if(window.parent.document){
          if(window.parent.document.loadFrame){
          window.parent.document.loadFrame();
        }
      }
      }
    }
   catch (error) {
  
   }
  });
 


  // Event Listeners for captures
  document.addEventListener("force-global-route", evt => {
    const { route, state } = evt.detail.value;
    navigate(route, { state: state ?? {} });
  });

  // START: This code is used for access to some model properties in state captures.
  
  // Returns a Numeric type fee. This return is not currency formatted by default.
  // Example: window.getFee("paper");
  window.getFee = (feeType, currencyFormatted) => {
    const { branding } = model;
    const fee = feeType.toLowerCase() === "paper" ? Payment.getPaperFee(branding) : Payment.getElectronicFee(branding);
    const total = Number(fee);
    const totalFormatted = BCMSCurrency.toCurrency(total);
    return currencyFormatted === true ? totalFormatted : total;
  }

  // Returns a Numeric type amount with a fee added. This return is not currency formatted by default.
  // Example: window.getAmountWithFee("paper", 100);
  window.getAmountWithFee = (feeType, amount, currencyFormatted) => {
    const { branding } = model;
    const fee = feeType.toLowerCase() === "paper" ? Payment.getPaperFee(branding) : Payment.getElectronicFee(branding);
    const total = Number(amount) + Number(fee);
    const totalFormatted = BCMSCurrency.toCurrency(total);
    return currencyFormatted === true ? totalFormatted : total;
  };
  // END: This code is used for access to some model properties in state captures.
  
  window.customEvent = (name, value) => document.dispatchEvent(new CustomEvent(name, { detail: { value } }));

  document.addEventListener("force-global-biller", evt => {
    BCMSRecord.globalControllerFor([Payment, Bill, Biller, BillerGroup, Autopay ], controller);
    const { crud, type, id, properties} = evt.detail.value;
    const defaultBiller = type === "known" ? BCMSDefaultModel.addBiller() : BCMSDefaultModel.biller();
    const existingBiller = id ? Biller.initByID(id).record : {};
    const billerID = id ?? Biller.nextID();

    const biller = Biller.init(
      crud === "update"
        ? { ...existingBiller, ...properties }
        : { ...defaultBiller.record, ...properties }
    );
    const operation = crud === "update"
      ? Biller.update(biller)
      : Biller.create({ ...biller.record, billerID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Biller ID: ${ billerID }`);
    }
  });

  document.addEventListener("force-global-payment", evt => {
    BCMSRecord.globalControllerFor([Payment, Bill, Biller, BillerGroup, Autopay ], controller);
    const { crud, id, properties, useFee } = evt.detail.value;
    const defaultPayment = BCMSDefaultModel.payment();
    const existingPayment = id ? Payment.initByID(id).record : {};
    const paymentID = id ?? Payment.nextID();

    const payment = Payment.init(
      crud === "update"
        ? { ...existingPayment, ...properties }
        : { ...defaultPayment.record, ...properties }
    );

    // Need to get the default payment date that is not a fee date    
    if(crud !== "update") {
      const { options } = controller.state.branding;

      if(useFee !== undefined && useFee) {
        const feeDate = Payment.getFeeDate(payment.to.paymentType);
        const feeAmount = payment.to.paymentType === "paper"
                ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22)
                : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22);

        payment.amount = BCMSCurrency.init(payment.amount.value + feeAmount.value);
        payment.date = feeDate;
      }
      else {
        const date = payment.date;

        if(properties.paymentDate === undefined && payment.to.paymentType === "paper") {
          Array.apply(null, Array(5)).forEach(i => date.nextBusinessDay());
        }
        if(Payment.getIfDateHasFee(date, payment.to.paymentType)) {
          date.nextBusinessDay();
        }

        payment.date = date;
      }
    }

    const operation = crud === "update"
      ? Payment.update(payment)
      : Payment.create({ ...payment.record, paymentID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Payment ID: ${ paymentID }`);
    }
  });

  document.addEventListener("force-global-bill", evt => {
    BCMSRecord.globalControllerFor([Payment, Bill, Biller, BillerGroup, Autopay ], controller);
    const { crud, id, properties } = evt.detail.value;
    const defaultBill = BCMSDefaultModel.bill();
    const existingBill = id ? Bill.initByID(id).record : {};
    const billID = id ?? Bill.nextID();

    const bill = Bill.init(
      crud === "update"
        ? { ...existingBill, ...properties }
        : { ...defaultBill.record, ...properties }
    );
    const operation = crud === "update"
      ? Bill.update(bill)
      : Bill.create({ ...bill.record, billID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Bill ID: ${ billID }`);
    }
  });

  document.addEventListener("force-global-autopay", evt => {
    BCMSRecord.globalControllerFor([Payment, Bill, Biller, BillerGroup, Autopay ], controller);
    const { crud, id, properties } = evt.detail.value;
    const defaultAutopay = BCMSDefaultModel.autopay();
    const existingAutopay = id ? Autopay.initByID(id).record : {};
    const autopayID = id ?? Autopay.nextID();

    const autopay = Autopay.init(
      crud === "update"
        ? { ...existingAutopay, ...properties }
        : { ...defaultAutopay.record, ...properties,  }
    );
    const operation = crud === "update"
      ? Autopay.update(autopay)
      : Autopay.create({ ...autopay.record, autopayID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Autopay ID: ${ autopayID }`);
    }
  });

  document.addEventListener("force-global-group", evt => {
    BCMSRecord.globalControllerFor([Payment, Bill, Biller, BillerGroup, Autopay ], controller);
    const { crud, id, properties } = evt.detail.value;
    const defaultGroup = BCMSDefaultModel.billerGroup();
    const existingGroup = id ? BillerGroup.initByID(id).record : {};
    const billerGroupID = id ?? BillerGroup.nextID();

    const group = BillerGroup.init(
      crud === "update"
        ? { ...existingGroup, ...properties }
        : { ...defaultGroup.record, ...properties }
    );
    const operation = crud === "update"
      ? BillerGroup.update(group)
      : BillerGroup.create({ ...group.record, billerGroupID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Biller Group ID: ${ billerGroupID }`);
    }
  });

  document.addEventListener("force-global-reminder", evt => {
    BCMSRecord.globalControllerFor([ Reminder ], controller);

    const { crud, id, properties } = evt.detail.value;
    const defaultReminder = BCMSDefaultModel.reminder();
    const existingReminder = id ? Reminder.initByID(id).record : {};
    const reminderID = id ?? Reminder.nextID();

    const reminder = Reminder.init(
      crud === "update"
        ? { ...existingReminder, ...properties }
        : { ...defaultReminder.record, ...properties }
    );
    const operation = crud === "update"
      ? Reminder.update(reminder)
      : Reminder.create({ ...reminder.record, reminderID });

    BCMSRecord.batch([ operation ], controller);

    if(crud === undefined || crud === "create") {
      console.log(`Created Reminder ID: ${ reminderID }`);
    }
  });

  document.addEventListener("force-global-profile", evt => {
    BCMSRecord.globalControllerFor([ Profile ], controller);

    const { properties } = evt.detail.value;
    const profile = Profile.init({ ...controller.state.profile, ...properties });
    
    BCMSRecord.batch([
      Profile.update(profile)
    ], controller);
  });

  return(
    <Page {...props} isActive="false">
      <ModalContainer modals={ modals }>
        <div className="page-view flex-container flex-vertical">
          <Branding id="demo_integrated_rxp22" value="false">
            <HeaderView />
          </Branding>

          <div className="page-content">
            {props.children}
          </div>
          
          <div class="copyright-container">
            <p>Copyright &copy;{ BCMSDate.init().format("Y") } Beavercreek Marketing. All Rights Reserved.</p>
          </div>

          <Branding id="demo_integrated_rxp22" value="false">
            <FooterView />
          </Branding>
        </div>
      </ModalContainer>
    </Page>
  );
}